import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ContextMenuItem, Modal, Typography } from '@volvo/vcdk-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { IFleet } from '@/service/vpc/types/IFleet';
import type { ColumnDef } from '@tanstack/react-table';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import FleetUnitCount from '@/components/FleetUnitCount';
import Table from '@/components/Table';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { getFleetsQueryOptions, queryKeys } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';
import { fleet } from '@/utils';

type Props = {
  fleetId: string;
  entityVersion: number;
};

const DeleteFleetModal: React.FC<Props> = ({ fleetId, entityVersion }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isLoading, fleetAccountId } = useFleetAccount();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { data: fleets } = useQuery(getFleetsQueryOptions({ fleetAccountId: fleetAccountId! }));

  const { mutateAsync: deleteFleet, isPending } = useMutation({
    mutationFn: (props: { fleetAccountId: string; fleetId: string; entityVersion: number }) => {
      return vpcService.Fleet.deleteFleet(props);
    },
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.getFleets], exact: false });
    },
  });

  const columns = [
    {
      accessorFn: (row) => row.name,
      id: 'Name',
      header: 'Name',
      cell: (info) => {
        const name = info.getValue<string>();
        return (
          <Typography variant="caption1" className="whitespace-nowrap">
            {fleet.parseFleetName(name).fleetName}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'unitCount',
      id: 'UnitCount',
      header: 'Units',
      cell: (info) => {
        const row = info.row.original;
        return <FleetUnitCount fleetId={row.fleetId} variant="caption1" />;
      },
    },
  ] as ColumnDef<IFleet>[];

  return (
    <>
      <ContextMenuItem disabled={isPending} onClick={() => setIsOpen(true)}>
        {t(($) => $.pages.account.fleet_overview.remove)}
      </ContextMenuItem>
      {isOpen &&
        createPortal(
          <Modal open={isOpen} onClose={() => setIsOpen(false)} width={700} maxWidth="90%">
            <span slot="header">{t(($) => $.components.modal.delete_fleet_modal.title)}</span>
            <div className="mb-6 flex flex-col gap-6">
              <Typography>{t(($) => $.components.modal.delete_fleet_modal.description)}</Typography>
              <Typography>{t(($) => $.components.modal.delete_fleet_modal.confirmation)}</Typography>
              <Table
                isLoading={isLoading}
                columns={columns}
                data={fleets?.filter((fleet) => fleet.fleetId === fleetId) || []}
                showHeader={false}
                showPagination={false}
                showSearch={false}
              />
            </div>
            <span slot="modalFooter">
              <div className="flex gap-4">
                <Button variant="secondary" size="medium" disabled={isPending} onClick={() => setIsOpen(false)}>
                  {t(($) => $.common.cancel)}
                </Button>
                <Button
                  variant="primary"
                  size="medium"
                  loading={isPending}
                  onClick={() => deleteFleet({ fleetAccountId: fleetAccountId!, fleetId, entityVersion })}
                >
                  {t(($) => $.components.modal.delete_fleet_modal.submit)}
                </Button>
              </div>
            </span>
          </Modal>,
          document.body
        )}
    </>
  );
};

export default withComponentErrorBoundary(DeleteFleetModal);
